import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon, Menu, X } from 'lucide-react';
import Footer from '../../footer';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import SiteLogo from '../../components/SiteLogo';
import { FlagIcon } from '../../components/common';
import BlockRenderer from './blocks/BlockRenderer';

/* ─── Fonts ───────────────────────────────────────────────────────────────
   Loaded as real <link rel="stylesheet"> tags rather than a CSS @import —
   @import can only be discovered after the browser parses the referencing
   stylesheet, adding an extra round-trip before the font request even
   starts. A <link> is discovered as soon as this module runs. The RTL
   families (Lalezar/Vazirmatn) are split into their own stylesheet and
   only fetched when the active language is actually RTL, so the majority
   LTR visitor doesn't pay for fonts they'll never render. */
const LTR_FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700;1,800&family=Inter:wght@300;400;500;600;700;800;900&display=swap';
const RTL_FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Lalezar&family=Vazirmatn:wght@300;400;500;600;700;800&display=swap';

const ensureStylesheetLink = (id, href) => {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
};

ensureStylesheetLink('cl-landing-fonts-ltr', LTR_FONTS_HREF);

/* ─── CSS ─────────────────────────────────────────────────────────────── */
const styleId = 'classic-landing-styles';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .cl-serif { font-family: 'Playfair Display', Georgia, serif; }
    .cl-sans { font-family: 'Inter', system-ui, sans-serif; }
    [dir="rtl"] .cl-serif { font-family: 'Lalezar', 'Vazirmatn', sans-serif; }
    [dir="rtl"] .cl-sans { font-family: 'Vazirmatn', system-ui, sans-serif; }
    @keyframes cl-fadeUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes cl-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
    @keyframes cl-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
    @keyframes cl-float2 { 0%, 100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(-14px) rotate(3deg); } }
    @keyframes cl-glow { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }
    .cl-up { animation: cl-fadeUp 0.8s cubic-bezier(0.22,1,0.36,1) both; }
    .cl-d1 { animation-delay: 0.1s; } .cl-d2 { animation-delay: 0.2s; } .cl-d3 { animation-delay: 0.3s; }
    .cl-d4 { animation-delay: 0.4s; }
    .cl-card { transition: all 0.4s cubic-bezier(0.22,1,0.36,1); }
    .cl-card:hover { transform: translateY(-4px); }
    .cl-scroll { display: flex; gap: 1.25rem; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none; padding-bottom: 0.5rem; }
    .cl-scroll::-webkit-scrollbar { display: none; }
    .cl-scroll > * { scroll-snap-align: start; flex-shrink: 0; }
  `;
  document.head.appendChild(style);
}

/* ─── Smokey Fluid Cursor Effect ──────────────────────────────────────── */
const FLUID_CANVAS_ID = 'cl-smokey-fluid';
const isTouchLikeDevice = () => {
  if (typeof window === 'undefined') return true;
  const mq = (q) => !!(window.matchMedia && window.matchMedia(q).matches);
  // smokey-fluid-cursor binds window touchmove + preventDefault (non-passive),
  // which blocks page scroll on phones. Skip the effect on touch / coarse pointers.
  // Also skip prefers-reduced-motion — it's purely decorative and runs a
  // continuous WebGL simulation, exactly what that setting asks to avoid.
  return mq('(pointer: coarse)') || mq('(hover: none)') || mq('(prefers-reduced-motion: reduce)');
};
const useSmokeyFluid = () => {
  useEffect(() => {
    if (isTouchLikeDevice()) {
      const existing = document.getElementById(FLUID_CANVAS_ID);
      if (existing) existing.remove();
      return;
    }
    let mounted = true;
    let cancelIdle = () => {};
    // Defer the chunk download + WebGL init until the browser is idle so
    // this purely decorative effect never competes with the initial
    // render/hydration path for network or main-thread time.
    const schedule = window.requestIdleCallback
      ? (fn) => { const id = window.requestIdleCallback(fn, { timeout: 2000 }); cancelIdle = () => window.cancelIdleCallback(id); }
      : (fn) => { const id = setTimeout(fn, 200); cancelIdle = () => clearTimeout(id); };

    schedule(() => {
      if (!mounted) return;
      let canvas = document.getElementById(FLUID_CANVAS_ID);
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.id = FLUID_CANVAS_ID;
        document.body.appendChild(canvas);
      }
      import('smokey-fluid-cursor').then(({ initFluid }) => {
        if (!mounted) return;
        initFluid({
          id: FLUID_CANVAS_ID,
          simResolution: 64,
          dyeResolution: 256,
          densityDissipation: 4.5,
          velocityDissipation: 3.5,
          pressure: 0.1,
          pressureIteration: 10,
          curl: 15,
          splatRadius: 0.2,
          splatForce: 2000,
          shading: false,
          colorUpdateSpeed: 8,
          transparent: true,
        });
        const el = document.getElementById(FLUID_CANVAS_ID);
        if (el) {
          el.style.zIndex = '0';
          el.style.opacity = '0.35';
          el.style.pointerEvents = 'none';
        }
      }).catch(() => {});
    });

    return () => {
      mounted = false;
      cancelIdle();
      const el = document.getElementById(FLUID_CANVAS_ID);
      if (el) el.remove();
    };
  }, []);
};

/* ─── Convert v1 flat content to v2 blocks ────────────────────────────── */
const convertV1ToBlocks = (content) => {
  if (!content) return { nav: {}, blocks: [] };

  const blocks = [];

  if (content.hero) {
    blocks.push({ id: 'hero_1', type: 'hero', enabled: true, data: content.hero });
  }
  if (content.stats) {
    blocks.push({ id: 'stats_1', type: 'stats', enabled: true, data: content.stats });
  }
  if (content.features) {
    blocks.push({ id: 'features_1', type: 'features', enabled: true, data: content.features });
  }
  if (content.how_it_works) {
    blocks.push({ id: 'how_it_works_1', type: 'how_it_works', enabled: true, data: content.how_it_works });
  }
  if (content.security) {
    blocks.push({ id: 'security_1', type: 'security', enabled: true, data: content.security });
  }
  if (content.cta) {
    blocks.push({ id: 'cta_1', type: 'cta', enabled: true, data: content.cta });
  }

  return { nav: content.nav || {}, blocks };
};

/* ─── Default blocks (when no API data) ───────────────────────────────── */
const defaultBlocks = [
  { id: 'hero_1', type: 'hero', enabled: true, data: {} },
  { id: 'stats_1', type: 'stats', enabled: true, data: {} },
  { id: 'features_1', type: 'features', enabled: true, data: {} },
  { id: 'how_it_works_1', type: 'how_it_works', enabled: true, data: {} },
  { id: 'security_1', type: 'security', enabled: true, data: {} },
  { id: 'cta_1', type: 'cta', enabled: true, data: {} },
];

/* ─── Main Component ─────────────────────────────────────────────────── */
const DarkElegance = ({ content }) => {
  const { t } = useTranslation('auth');
  const { isDark, setColorTheme } = useTheme();
  const { language, languages, setLanguage, isRTL } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);
  useSmokeyFluid();

  useEffect(() => {
    if (isRTL) ensureStylesheetLink('cl-landing-fonts-rtl', RTL_FONTS_HREF);
  }, [isRTL]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => setColorTheme(isDark ? 'light' : 'dark');

  // Resolve block structure — support both v1 (flat) and v2 (blocks) formats.
  // Which blocks render and in what order is real per-instance config from
  // the API; the labels within each block are handled by the block itself.
  let blocks;
  if (!content) {
    blocks = defaultBlocks;
  } else if (content.version === 2) {
    blocks = content.blocks || defaultBlocks;
  } else {
    blocks = convertV1ToBlocks(content).blocks;
  }

  // Nav button labels are static UI chrome — always translated, never CMS content.
  const navLogin = t('landing.logIn', 'Log In');
  const navSignup = t('landing.getStarted', 'Join Free');
  const currentLanguage = languages.find(l => l.code === language);
  const currentLangName = currentLanguage?.nativeName || 'EN';

  const bg = 'bg-white dark:bg-[#0a0a0a]';

  const LanguagePicker = ({ buttonClassName }) => (
    <div className="relative">
      <button
        onClick={() => setShowLangPicker(!showLangPicker)}
        className={buttonClassName}
        title={currentLangName}
      >
        <FlagIcon code={currentLanguage?.code} className="w-5 h-4" />
      </button>
      {showLangPicker && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowLangPicker(false)} />
          <div className="absolute z-50 top-full mt-2 end-0 w-44 bg-white dark:bg-[#151515] rounded-xl shadow-lg border border-gray-200 dark:border-white/[0.08] overflow-hidden">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setShowLangPicker(false); }}
                className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm transition-colors ${
                  language === lang.code
                    ? 'bg-[var(--color-primary-50)] dark:bg-white/[0.06] text-[var(--color-primary-600)] font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04]'
                }`}
              >
                <FlagIcon code={lang.code} className="w-5 h-4" />
                <span dir="ltr">{lang.nativeName}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={`cl-sans min-h-screen ${bg} text-gray-900 dark:text-white overflow-x-hidden`}>
      {/* ═══ NAVIGATION ═══ */}
      <nav className={`fixed top-0 start-0 end-0 z-50 transition-all duration-500 ${
        scrollY > 50
          ? 'bg-white/80 dark:bg-[#0a0a0a]/80 backdrop-blur-2xl border-b border-gray-200/50 dark:border-white/[0.06]'
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="h-[72px] flex items-center justify-between">
            <Link to="/" className="flex-shrink-0">
              <SiteLogo className="h-8 sm:h-9" />
            </Link>
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                title={isDark ? t('landing.switchToLight', 'Switch to light mode') : t('landing.switchToDark', 'Switch to dark mode')}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <LanguagePicker
                buttonClassName="p-2.5 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
              />
              <Link to="/login" className="px-5 py-2.5 text-[14px] font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-all">
                {navLogin}
              </Link>
              <Link to="/register" className="px-7 py-2.5 rounded-full text-[14px] font-semibold text-white dark:text-black bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 transition-all">
                {navSignup}
              </Link>
            </div>
            <div className="md:hidden flex items-center gap-1">
              <button onClick={toggleTheme} className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 rounded-xl transition-colors">
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <LanguagePicker
                buttonClassName="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 rounded-xl transition-colors"
              />
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 rounded-xl transition-colors">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 dark:bg-[#0a0a0a]/95 backdrop-blur-2xl border-t border-gray-200 dark:border-white/[0.06] px-5 py-5 space-y-3 cl-up">
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center py-3 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors">{navLogin}</Link>
            <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center py-3 text-sm font-semibold text-white dark:text-black bg-gray-900 dark:bg-white rounded-full transition-colors">{navSignup}</Link>
          </div>
        )}
      </nav>

      {/* ═══ BLOCKS ═══ */}
      <BlockRenderer blocks={blocks} />

      <Footer />
    </div>
  );
};

export default DarkElegance;
